import React, { useState, useEffect } from 'react'
import { View, StyleSheet, Platform, Image } from 'react-native'
import { MenuItem, Text, Button, Icon, ProgressBar, Spinner, Modal } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FunnelStructure from './FunnelStructure'
import { atomWithAsyncStorage, signupInProgressAtom } from '../../global/AtomsProvider'
import { useAtom, useSetAtom } from 'jotai'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { useColors } from '../../utils/useColors'
import { createNewUserAndOrganization, auth } from '../../global/Firebase/firebase'
import * as Sentry from "@sentry/react-native"
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import HeaderIconButton from '../../components/HeaderIconButton'
import HeaderImageView from '../../components/HeaderImageView'
import ReviewsSlider from '../../components/ReviewsSlider'
import ConsentCheckbox from '../../components/ConsentCheckbox'
import PhoneField from '../../components/PhoneField'
import Loader from '../../components/Loader'
import { usePostHog } from 'posthog-react-native'
import { CURRENCIES } from '../../utils/currencies'
import { captureSignupCompleted } from '../../utils/analyticsIdentity'
import { buildProvenanceProperties } from '../../utils/buildProvenance'
import { resolveWebAcquisitionBounded, buildAcquisitionProperties } from '../../utils/webAcquisition'

export const funnelAnswersAtom = atomWithAsyncStorage('funnelAnswers', {})

// Re-export for backward compatibility
export { STATIC_FIELDS } from '../../utils/funnelConstants'
import { STATIC_FIELDS, ownsContinueButton } from '../../utils/funnelConstants'
import { WHATSAPP_OPT_IN_AT_FIELD } from '../../utils/whatsappOnboardingConsent'

// Generate test funnel answers for skip mode
const generateTestFunnelAnswers = () => {
  const timestamp = Date.now()
  return {
    businessType: { id: 'retail' },
    clientType: { id: 'b2c' },
    monthlySales: { id: 'from1kTo10k' },
    inventorySize: { id: '50to200' },
    currency: CURRENCIES.find(c => c.id === 'kzt'),
    hasBranches: { id: 'no' },
    employeeCount: { id: '1-5' },
    priorExperience: { id: 'no' },
    salesReports: [{ id: 'overall' }, { id: 'byProduct' }],
    inventoryTrackingNeeds: { id: 'basic' },
    inventoryOperationNeeds: [{ id: 'supplies' }, { id: 'writeOffs' }],
    barcodeNeeds: { id: 'no' },
    wantClientTracking: { id: 'no' },
    wantOnlineCatalog: { id: 'no' },
    additionalFeatures: [{ id: 'none' }],
    setupOption: { id: 'no' },
    trafficSource: { id: 'webSearch' },
    orgName: `Test Org ${timestamp}`,
    userName: 'Test User',
    phone: '+77001234567',
    email: `supabase+${timestamp}@test.com`,
    password: 'temporary',
  }
}

export default function FunnelScreen ({ navigation, route }) {
  const { t } = useTranslation()
  const colors = useColors()
  let screenIndex = 0
  if (route.params?.screenIndex !== undefined) screenIndex = route.params.screenIndex
  const { control, handleSubmit, setValue, formState: { errors }, watch } = useForm()
  const [screenConfig, setScreenConfig] = useState(FunnelStructure?.[screenIndex] || FunnelStructure?.[0])
  const [searchQuery, setSearchQuery] = useState('')
  const [funnelAnswers, setFunnelAnswers] = useAtom(funnelAnswersAtom)
  const phoneInput = React.createRef()
  const [isLoading, setIsLoading] = useState(false)
  const [isSkipMode, setIsSkipMode] = useState(false)
  const postHog = usePostHog()
  const setSignupInProgress = useSetAtom(signupInProgressAtom)
  // ASO-AN-001: `user_signed_up` is the ONE canonical successful-signup event, and it must fire at
  // most once per signup. `registerOrg` has three entry points — the last funnel screen's submit,
  // the skipped-condition branch in the effect below, and the test-mode Skip button — and an effect
  // that re-runs (a re-render, a locale change) can reach it again. Without this latch the funnel
  // could double-count a conversion, which is worse than under-counting because it looks plausible.
  const signupCapturedRef = React.useRef(false)

  // Check for skip mode from URL params (web only)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location?.search) {
      const urlParams = new URLSearchParams(window.location.search)
      const skipValue = urlParams.get('skip') === 'true'
      console.log('Skip mode check:', { search: window.location.search, skipValue, screenIndex })
      setIsSkipMode(skipValue)
    }
  }, [])

  const handleSkip = async () => {
    const testAnswers = generateTestFunnelAnswers()
    setFunnelAnswers(testAnswers)
    registerOrg(testAnswers)
  } 
  useEffect(() => {
    navigation.setOptions({
      headerTintColor: 'black',
      headerShadowVisible: false,
      headerStyle: {
        backgroundColor: colors.background,
        elevation: 0,
      },
      headerLeft: navigation.getState()?.index === 0 ? (() => (
        <HeaderIconButton
          onPress={() => {
            navigation.navigate('Login')
          }}
          iconName='arrow-back-outline'
        />
      )) : undefined,
      headerRight: () => (
        <HeaderImageView
          source={require('../../assets/almaty.png')}
        />
      )
    })
  }, [])

  // conditions doesn't work - funnel answers are empty
  useEffect(() => {
    if (!screenConfig || !FunnelStructure) return

    if (screenConfig.condition?.(funnelAnswers) === false) {
      if (FunnelStructure.length === screenIndex+1) registerOrg()
      else navigation.push('Funnel', { screenIndex: screenIndex+1 })
    }
    if (screenConfig.content?.some(content => content.type === 'phoneField')) {
      phoneInput.current?.focus() // hack to autofocus it
    }
    if (screenConfig?.id) {
      sendFBAnalyticsEvent('funnel_screen_viewed', { screen: screenConfig?.id, screenIndex: screenIndex })
      postHog.screen('funnel_' + screenConfig?.id)
    }
  }, [screenIndex, screenConfig])


  const AccessoryIcon = (props, name, color=undefined) => (
    color ? 
      <Icon
        fill={color}
        {...props}
        name={name}
      />
    :
      <Icon
        {...props}
        name={name}
      />
  )

  const onErrors = (errors) => {
    console.log('error', errors)
  }

  const onSubmit = async (data) => {
    let updatedFunnelAnswers
    setFunnelAnswers(funnelAnswers => {
      updatedFunnelAnswers = {...funnelAnswers,  ...data}
      return updatedFunnelAnswers
    })
    if (FunnelStructure.length === screenIndex+1) registerOrg(updatedFunnelAnswers)
    else navigation.push('Funnel', { screenIndex: screenIndex+1 })
  }

  const registerOrg = async (funnelAnswers) => {
    setIsLoading(true)
    setSignupInProgress(true) // Prevent data loading until Supabase user/org is created
    let capturedThisRun = false
    try {
      const created = await createNewUserAndOrganization(funnelAnswers)
      sendFBAnalyticsEvent('signup_complete')
      // ASO-AN-001: identity FIRST, then the event — enforced inside `captureSignupCompleted`, not
      // by the order of two lines here. This used to be a bare `postHog.capture('user_signed_up')`
      // from a screen in AuthStack, where the only `identify()` in the app (AppStack, driven by the
      // org snapshot) was still several async hops away — so all 40 iOS signups in August were
      // recorded as `$is_identified: false` and could not be cohorted to anything the user later
      // did. The uid comes from the value we just awaited, with `auth.currentUser` as the fallback
      // for a bundle that predates the return value.
      if (!signupCapturedRef.current) {
        signupCapturedRef.current = true
        const uid = created?.uid || auth.currentUser?.uid || null
        // WEB-ATTR-2026-001: where this person came from, resolved from the marketing site's
        // first-party cookie or the `?atlas_attr=` handoff on the signup link. Awaited HERE rather
        // than read from a ref, because this is the one event whose acquisition properties cannot
        // be added later — PostHog events are immutable, and `user_signed_up` is the join between a
        // campaign and everything the organization goes on to do.
        //
        // It resolves to null on native and for a signup that carries no attribution at all; in
        // both cases `buildAcquisitionProperties` still returns `signup_platform`, which is the
        // denominator the ≥99% completeness query needs in order to exist.
        //
        // ⚠️ BOUNDED, AND THE BOUND IS THE POINT. This await sits between a SUCCEEDED
        // `createNewUserAndOrganization` and the navigation away from the spinner, and
        // `resolveWebAcquisition` awaits AsyncStorage. A storage call that never settles never
        // rejects, so a bare `.catch()` cannot save it — the account exists and the person waits
        // forever. `captureSignupCompleted` below is capped for exactly this reason; so is this.
        const acquisition = await resolveWebAcquisitionBounded()
        const captured = await captureSignupCompleted(postHog, {
          uid,
          organizationId: created?.organizationId,
          properties: buildAcquisitionProperties(acquisition, { platform: Platform.OS }),
          // Signing up on a device that is still identified as somebody else resets PostHog first,
          // which wipes the build provenance super properties. Hand them back so the signup event
          // itself — the one this ticket exists for — still carries them.
          superProperties: buildProvenanceProperties(),
        })
        capturedThisRun = captured
        if (!captured) {
          // A signup that cannot be attributed is a reportable anomaly, not something to emit
          // anonymously and let the funnel look healthy. The signup itself already succeeded.
          Sentry.captureMessage('signup completed but analytics identity was unavailable', {
            level: 'warning',
            tags: { signup_identity_missing: true },
            extra: { hasUid: Boolean(uid) },
          })
        }
      }
      // navigation.navigate('Paywall', { showCloseButton: false })
      setSignupInProgress(false) // Supabase user/org created, allow data loading
      navigation.navigate('Home')
    } catch(error) {
      // The latch guards against DOUBLE-firing a successful signup, not against retrying a failed
      // one. `createNewUserAndOrganization` is explicitly retry-safe (it signs in when the auth
      // account already exists), so a user who fixes the error and submits again must still be
      // counted exactly once — but ONLY when this run did not already emit the event. A throw from
      // anything AFTER the capture (the navigate, a state update) must not re-open the latch, or
      // the retry counts the same conversion twice.
      if (!capturedThisRun) signupCapturedRef.current = false
      setSignupInProgress(false)
      alert(error.message)
      Sentry.captureException(error)
    }
    setIsLoading(false)
  }

  const handleSearch = (text) => {
    setSearchQuery(text)
  }

  // WA-ONBOARD-001: `ownsButton` says whether THIS field draws the screen's continue button.
  // Every input renderer used to draw one unconditionally, which was fine while every screen had
  // exactly one input. The phone screen now also carries the WhatsApp opt-in, and a button drawn by
  // the phone field would sit ABOVE the consent it commits — see `ownsContinueButton`.
  const component = (onChange, onBlur, value, {id, type, props}, ownsButton = true) => {
    let localizedOptions = props.localization ? props.localization(props.options, t) : props.options
    switch (type) {
      case 'title':
      return <Text category='h4' style={{ marginBottom: 20 }}>{t(props.text)}</Text>
      case 'titleWithSubtitle':
        return (
          <View>
            <Text category='h3' style={{ marginBottom: 5, textAlign: props.centered ? 'center' : 'left' }}>{t(props.title)}</Text>
            <Text category='s1' style={{ marginBottom: 20, textAlign: props.centered ? 'center' : 'left' }}>{t(props.subtitle)}</Text>
          </View>
        )
      case 'image':
        return (
          <Image 
            source={props.source}
            resizeMode='contain'
            style={{ width: '100%', height: 250, marginBottom: 30, marginTop: 10, alignSelf: 'center' }} 
          />
        )
      case 'button':
        return <Button onPress={() => handleSubmit(onSubmit, onErrors)()}>{t(props.text)}</Button>
      case 'singleSelect':
        return (
          <View>
            {props.withSearch && <StyledTextInput
              style={{ marginBottom: 10 }}
              placeholder={t('search')}
              onBlur={onBlur}
              onChangeText={handleSearch}
              returnKeyType='done'
            />}
            {props.withSearch ? 
              localizedOptions.filter(option => option.text.toLowerCase().includes(searchQuery.toLowerCase())).map((option, index) => (
                <MenuItem
                  key={index}
                  style={{marginBottom: 5, borderRadius: 5}}
                  title={option.text}
                  accessoryRight={(props) => AccessoryIcon(props, 'arrow-ios-forward')}
                  accessoryLeft={option.icon ? (props) => AccessoryIcon(props, option.icon) : undefined}
                  onPress={() => {
                  onChange(option)
                    handleSubmit(onSubmit, onErrors)()
                  }}
                />
              ))
            :
              localizedOptions.map((option, index) => (
                <MenuItem
                  key={index}
                  style={{marginBottom: 5, borderRadius: 5}}
                  title={option.text}
                  accessoryRight={(props) => AccessoryIcon(props, 'arrow-ios-forward')}
                  accessoryLeft={option.icon ? (props) => AccessoryIcon(props, option.icon) : undefined}
                  onPress={() => {
                    onChange(option)
                    handleSubmit(onSubmit, onErrors)()
                  }}
                />
              ))
            }  
          </View>
        )
      case 'multiSelect':
        return (
          <View>
            {localizedOptions.map((option, index) => (
              <MenuItem
                key={option.id}
                style={{marginBottom: 5, borderRadius: 5}}
                title={option.text}
                accessoryRight={(props) => AccessoryIcon(props, 'arrow-ios-forward')}
                accessoryLeft={(props) => AccessoryIcon(props, value?.some?.(item => item.id === option.id) ? 'checkmark-circle-outline' : 'radio-button-off-outline', value?.some?.(item => item.id === option.id) ? colors.primary : undefined)}
                onPress={() => {
                  if (value?.some?.(item => item.id === option.id)) {
                    onChange(value?.filter?.
                      (item => item.id !== option.id))
                  } else {
                    if (value) {
                      onChange([...value, option])
                    } else {
                      onChange([option])
                    }
                  }
                }}
              />
            ))}  
            {ownsButton && (
              <Button style={{marginTop: 15}} onPress={() => handleSubmit(onSubmit, onErrors)()}>
                {t('continue')}
              </Button>
            )}
          </View>
        )
      case 'textField':
        return (
          <View>
            <StyledTextInput
              style={{ marginBottom: 20 }}
              placeholder={t(props.placeholder)}
              onBlur={onBlur}
              onChangeText={onChange}
              returnKeyType='done'
              onSubmitEditing={() => handleSubmit(onSubmit, onErrors)()}
              autoFocus={true}
            />
            {ownsButton && (
              <Button onPress={() => handleSubmit(onSubmit, onErrors)()}>
                {t('continue')}
              </Button>
            )}
          </View>
        )
      case 'numberField':
        return (
          <View>
            <StyledTextInput
              style={{ marginBottom: 20 }}
              placeholder={t(props.placeholder)}
              onBlur={onBlur}
              onChangeText={onChange}
              keyboardType='decimal-pad'
              returnKeyType='done'
              onSubmitEditing={() => handleSubmit(onSubmit, onErrors)()}
              autoFocus={true}
            />
            {ownsButton && (
              <Button onPress={() => handleSubmit(onSubmit, onErrors)()}>
                {t('continue')}
              </Button>
            )}
          </View>
        )
      case 'emailField':
        return (
          <View>
            <StyledTextInput
              style={{ marginBottom: 20 }}
              placeholder={t(props.placeholder)}
              onBlur={onBlur}
              onChangeText={onChange}
              keyboardType='email-address'
              textContentType="emailAddress"
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType='done'
              onSubmitEditing={() => handleSubmit(onSubmit, onErrors)()}
              autoFocus={true}
            />
            {ownsButton && (
              <Button onPress={() => handleSubmit(onSubmit, onErrors)()}>
                {t('continue')}
              </Button>
            )}
          </View>
        )
      case 'phoneField':
        // WA-ONBOARD-001: extracted so the number is harvested on CHANGE rather than only from
        // inside this field's own continue button. That coupling was invisible until the WhatsApp
        // opt-in took the button, at which point the funnel dead-ended here. See PhoneField.js.
        return (
          <PhoneField
            inputRef={phoneInput}
            onChange={onChange}
            onSubmit={() => handleSubmit(onSubmit, onErrors)()}
            showContinueButton={ownsButton}
          />
        )
      case 'passwordField':
        return (
          <View>
            <StyledTextInput
              style={{ marginBottom: 20 }}
              placeholder={t(props.placeholder)}
              onBlur={onBlur}
              onChangeText={onChange}
              textContentType="password"
              autoCapitalize="none"
              autoCorrect={false}
              secureTextEntry={true}
              returnKeyType='done'
              onSubmitEditing={() => handleSubmit(onSubmit, onErrors)()}
              autoFocus={true}
            />
            {ownsButton && (
              <Button onPress={() => handleSubmit(onSubmit, onErrors)()}>
                {t('continue')}
              </Button>
            )}
          </View>
        )
      case 'checkboxField':
        // WA-ONBOARD-001. Deliberately NOT followed by a continue button: this is an optional
        // opt-in that sits above the screen's own submit, so ticking it must not advance the
        // funnel and leaving it alone must not block it.
        return (
          <View>
            <ConsentCheckbox
              value={value}
              // WA-ONBOARD-001: the checkbox reports WHEN it was ticked as well as WHAT was
              // chosen. `setValue` puts the instant on its own funnel field, because
              // react-hook-form gives this field one value and the ledger needs both — and the
              // submit that would otherwise supply the timestamp is three screens later.
              onChange={(checked, at) => {
                onChange(checked)
                setValue(WHATSAPP_OPT_IN_AT_FIELD, checked ? at : undefined)
              }}
              label={props.label}
              disclosure={props.disclosure}
            />
            {ownsButton && (
              <Button onPress={() => handleSubmit(onSubmit, onErrors)()}>
                {t('continue')}
              </Button>
            )}
          </View>
        )
      case 'reviewsSlider':
        return <ReviewsSlider reviews={props.reviews} withBackground={props.withBackground} />
      case 'loader':
        return <Loader duration={props.duration} action={() => handleSubmit(onSubmit, onErrors)()} />
      default:
        return <Text>{t('unknownComponentType', { type })}</Text>
    }
  }

  // Guard against undefined screenConfig (can happen due to require cycles)
  if (!screenConfig || !FunnelStructure) {
    return (
      <View style={{flex: 1, justifyContent: 'center', alignItems: 'center'}}>
        <Spinner size="large" />
      </View>
    )
  }

  if (isLoading) {
    return (
      <Modal
        visible={true}
        backdropStyle={{backgroundColor: 'rgba(0, 0, 0, 0.3)'}}
      >
        <View style={{flex: 1, justifyContent: 'center', alignItems: 'center'}}>
          <Text category='c2' style={{marginBottom: 10, textAlign: 'center'}}>{t('funnel.creatingOrg')}</Text>
          <View style={{alignItems: 'center'}}><Spinner size="large" /></View>
        </View>
      </Modal>
    )
  }

  return (
    <KeyboardAwareScrollView contentContainerStyle={styles.topContainer} keyboardDismissMode='on-drag' keyboardShouldPersistTaps='handled'>
      <View style={styles.container}>
        <ProgressBar
          style={{ width: '100vw', height: 3, marginTop: Platform.OS === 'web' ? 0 : 5, marginBottom: 20 }}
          progress={(screenIndex + 1) / FunnelStructure.length}
          size='small'
          animating={false}
        />
        {isSkipMode && screenIndex === 0 && (
          <View style={{ paddingHorizontal: 20, marginBottom: 20 }}>
            <Button
              status='warning'
              appearance='outline'
              onPress={handleSkip}
            >
              Skip (Test Mode)
            </Button>
          </View>
        )}
        {screenConfig.content.map((content, index) => (
          <View key={content.id} style={{paddingHorizontal: 20}}> 
            <Controller
              key={content.id}
              control={control}
              // WA-ONBOARD-001: `defaultValue` is what makes an unticked consent box report a
              // literal `false` on submit instead of `undefined`. Fields that declare none keep
              // react-hook-form's own behaviour, so nothing else changes.
              defaultValue={content.props.defaultValue}
              rules={{
                required: STATIC_FIELDS.includes(content.type) ? false : true,
                ...content.props.validationRules
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                component(onChange, onBlur, value, content, ownsContinueButton(screenConfig.content, index))
              )}
              name={content.id}
            />
            {errors[content.id]?.type === 'required' && <Text style={{marginTop: 10}} status='danger'>{t('notAllFieldsFilled')}</Text>}
            {errors[content.id]?.type === 'minLength' && <Text style={{marginTop: 10}} status='danger'>{t('minLengthError', { minLength: content.props.validationRules.minLength })}</Text>}
            {errors[content.id]?.type === 'maxLength' && <Text style={{marginTop: 10}} status='danger'>{t('maxLengthError', { maxLength: content.props.validationRules.maxLength })}</Text>}
            {errors[content.id]?.type === 'min' && <Text style={{marginTop: 10}} status='danger'>{t('minError', { min: content.props.validationRules.min })}</Text>}
            {errors[content.id]?.type === 'max' && <Text style={{marginTop: 10}} status='danger'>{t('maxError', { max: content.props.validationRules.max })}</Text>}
            {errors[content.id]?.type === 'pattern' && <Text style={{marginTop: 10}} status='danger'>{t('patternError', { type: content.props.validationRules.pattern.message })}</Text>}
            {errors[content.id]?.type === 'validate' && errors[content.id]?.message === 'emailTaken' && <Text style={{marginTop: 10}} status='danger'>{t('emailTaken')}</Text>}
          </View>
        ))}
      </View>
      <View style={{height: 100}}></View>

    </KeyboardAwareScrollView>
  )
}

const styles = StyleSheet.create({
  topContainer: {
    // flex: 1, 
    alignItems: 'center',
  },
  container: {
    flex: 1, 
    minWidth: '100%',
    maxWidth: 800,
  },
})